import { SyntheticEvent, memo } from 'react';

import { useMounted } from '@hooks/useMounted';

import Banner, { BannerSize } from '@components/common/Banner';
import ContentByProfileFromGallery from '@components/outlook/ContentByProfileFromGallery';
import Divider from '@components/outlook/Divider';
import ProfileGallery from '@components/outlook/ProfileGallery';
import ProfileWithMessage from '@components/outlook/ProfileWithMessage';

import { IContentGalleryContent, IRenderedComponent } from '@base/outlookModel';
import { setFlow } from '@utils/misc';

import ContentGalleryWithHeader from './ContentGalleryWithHeader';
import PlaCardBanner from './PlacardBanner';
import ProfileByChoiceFromGallery from './ProfileByChoiceFromGallery';
import ProfileByInfoCardFormGallery from './ProfileByInfoCardFormGallery';
import ProfileByInfoPlacardFormGallery from './ProfileByInfoPlacardFormGallery';
import ProfileByInfoPlacardFormList from './ProfileByInfoPlacardFormList';
import ProfileByInfoSectionFromList from './ProfileByInfoSectionFromList';
import SwipableTagList from './SwipableTagList';

interface OutlookComponentProps {
  component: IRenderedComponent;
  onClickSituation?: (
    e: SyntheticEvent,
    content: IContentGalleryContent
  ) => void;
  onClickProfile?: (id: number) => void;
  handleCreateChatRoom?: (id: number) => void;
  onClickSituationById?: (situationId: number) => void;
  onClickGround?: (id: number, grounddId: number) => void;
  onClickGroundCharacter?: (characterId: number) => void;
  onClickChoice?: (idx: number, situationId: number) => void;
  order: string | number;
}
const OutlookComponent = memo(
  ({
    component,
    order,
    onClickSituation,
    onClickProfile,
    handleCreateChatRoom,
    onClickGroundCharacter,
    onClickGround,
    onClickSituationById,
    onClickChoice,
  }: OutlookComponentProps) => {
    // const { isTablet } = useGlobalContext();
    const isMounted = useMounted();
    const handleClickProfile = (id: number) => {
      if (onClickProfile) {
        onClickProfile(id);
      }
    };
    const handleClickGround = (id: number, groundId: number) => {
      setFlow({ name: '그라운드 큐레이션' });
      if (onClickGround) {
        onClickGround(id, groundId);
      }
    };

    //component.view_type === 'profile_by_choice_form_gallery
    if (component.view_type === 'profile_by_choice_form_gallery') {
      return (
        <ProfileByChoiceFromGallery
          data={component.data}
          componentKey={component?.key}
          key={order}
          onClickGroundCharacrer={onClickGroundCharacter}
          onClickChoice={onClickChoice}
        />
      );
    }

    if (component.view_type === 'tag_list' && isMounted) {
      return (
        <SwipableTagList
          tags={component.data.tags}
          key={order}
          title={component.data.title}
          onClickCard={handleCreateChatRoom}
          onClickProfile={onClickProfile}
          rowCount={component.data.row_count}
          contentCount={component.data.content_count}
        />
      );
    }

    if (component.view_type === 'divider_line') {
      return <Divider data={component.data} />;
    }
    if (component.view_type === 'full_screen_banner') {
      return (
        <Banner
          key={order}
          name="play-full-screen=banner"
          size={BannerSize.playHome}
          items={component.data.banners}
          isBorder={false}
        />
      );
    }
    if (component.view_type === 'profile_gallery') {
      return <ProfileGallery data={component.data} />;
    }
    if (component.view_type === 'content_gallery_with_header') {
      return (
        <ContentGalleryWithHeader
          key={order}
          data={component.data}
          onClickProfile={handleClickProfile}
          onClickSituation={onClickSituation && onClickSituation}
        />
      );
    }
    if (component.view_type === 'profile_with_message') {
      return (
        <ProfileWithMessage
          key={order}
          data={component.data}
          onClickProfile={handleClickProfile}
        />
      );
    }
    if (component.view_type === 'content_by_profile_form_gallery') {
      return (
        <ContentByProfileFromGallery
          key={order}
          order={order}
          data={component.data}
          onClickGround={handleClickGround}
          onClickSituation={onClickSituationById && onClickSituationById}
          onClickProfile={handleClickProfile}
        />
      );
    }
    if (component.view_type === 'profile_by_info_card_form_gallery') {
      return (
        <ProfileByInfoCardFormGallery
          key={order}
          data={component.data}
          onClickCard={handleCreateChatRoom}
          onClickProfile={onClickProfile}
          componentKey={component.key}
        />
      );
    }
    if (component.view_type === 'profile_by_info_placard_form_gallery') {
      return (
        <ProfileByInfoPlacardFormGallery
          key={order}
          data={component.data}
          onClickCard={handleCreateChatRoom}
          onClickProfile={onClickProfile}
          componentKey={component.key}
        />
      );
    }
    if (component.view_type === 'profile_by_info_placard_form_list') {
      return (
        <ProfileByInfoPlacardFormList
          key={order}
          data={component.data}
          onClickSituationById={onClickSituationById && onClickSituationById}
        />
      );
    }
    if (component.view_type === 'placard_banner') {
      return <PlaCardBanner key={order} data={component.data} />;
    }
    if (component.view_type === 'profile_by_info_section_form_list') {
      return (
        <ProfileByInfoSectionFromList
          key={order}
          data={component.data}
          componentKey={component?.key}
        />
      );
    }

    return null;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.component === nextProps.component &&
      prevProps.onClickSituation === nextProps.onClickSituation &&
      prevProps.onClickProfile === nextProps.onClickProfile
    );
  }
);

OutlookComponent.displayName = 'OutlookComponent';

export default memo(OutlookComponent);
